/* Lab 1.5 · 逐帧翻一段真机示范.

   The data is the LeRobot dataset lerobot/svla_so101_pickplace (data.json, frames/*.jpg, from make_data.py):
   every episode's six joint readings at 10 Hz, and camera frames for two episodes. For each joint, the
   grey band is where the 50 episodes usually are at that point of their own progress (normalised time,
   mean ± 1 standard deviation); a reading more than 2 standard deviations out is marked amber.

   The scripted take (9.5 s, one segment of episode 1.5):
     0–6.3   episode 0 plays: both cameras and the six curves move together; the gripper closes on the brick
     6.3–9.5 episode 47, the one furthest from the rest: the frames jump to where its shoulder leaves the band */
import * as THREE from "three";
import { q, recorded, createStage, floor, run, clamp, span, el } from "../kit/stage";
import { Chart } from "../kit/chart";
import { localizePage, tr, type Dict } from "../kit/i18n";
import DATA from "./data.json";
import STRINGS from "./i18n.json";

localizePage(STRINGS as Dict);

interface Episode { index: number; frames: number; seconds: number; state: number[][] }
interface Sprite { file: string; frames: number; cols: number; w: number; h: number }
const episodes = DATA.episodes as Episode[];
const sprites = DATA.sprites as Record<string, Sprite>;
const band = DATA.band as { mean: number[][]; std: number[][] };
const drift = DATA.drift;
const NAMES: [string, string][] = [["底座转", "shoulder_pan"], ["肩抬", "shoulder_lift"], ["肘弯", "elbow_flex"], ["腕弯", "wrist_flex"], ["腕转", "wrist_roll"], ["夹爪", "gripper"]];
const HZ = DATA.hz;

// --- the scripted take
function scripted(t: number): { ep: number; u: number } {
  if (t < 6.3) return { ep: 0, u: span(t, .2, 6.2) };
  return { ep: drift.episode, u: .2 + (drift.at - .2 + .04) * span(t, 6.5, 8.6) };
}

// --- a quiet stage behind the panels
const canvas = el<HTMLCanvasElement>("stage");
const { scene, camera, render } = createStage(canvas);
floor(scene);
camera.position.set(0, 1.6, 2.4);
camera.lookAt(new THREE.Vector3(0, 0, 0));

// --- joint rows: a small chart each
const rows = el("rows");
const charts = NAMES.map(([zh, en], j) => {
  rows.insertAdjacentHTML("beforeend", `<div class="jrow" id="j${j}"><div class="name">${tr(zh)}<small>${en}</small></div><div id="c${j}"></div><div class="val" id="v${j}">—</div></div>`);
  const all = episodes.flatMap(e => e.state.map(s => s[j]));
  const lo = Math.min(...all), hi = Math.max(...all), pad = (hi - lo) * .08;
  return { chart: new Chart(el("c" + j), { x: [0, 1], y: [lo - pad, hi + pad] }, 420, 84, { l: 2, r: 2, t: 4, b: 4 }), range: hi - lo };
});

// --- camera frames from the sprite sheets (preloaded before the clock starts)
const setFrame = (id: string, noId: string, key: string, u: number): void => {
  const s = sprites[key], img = el(id), no = el(noId);
  if (!s) { img.style.backgroundImage = ""; no.textContent = tr("这一段没有打包画面（只打包了第 0 段和最不一样的一段）；关节读数照常显示。"); return; }
  no.textContent = "";
  const i = clamp(Math.round(u * (s.frames - 1)), 0, s.frames - 1), rowsN = Math.ceil(s.frames / s.cols);
  img.style.backgroundImage = `url(${s.file})`;
  img.style.backgroundSize = `${s.cols * 100}% ${rowsN * 100}%`;
  img.style.backgroundPosition = `${(i % s.cols) / (s.cols - 1) * 100}% ${rowsN > 1 ? Math.floor(i / s.cols) / (rowsN - 1) * 100 : 0}%`;
};

// --- live controls
const user = { ep: 0, u: 0, playing: true };
const epIn = el<HTMLInputElement>("ep"), frIn = el<HTMLInputElement>("fr");
epIn.addEventListener("input", () => { user.ep = Number(epIn.value); user.u = 0; });
frIn.addEventListener("input", () => { user.u = Number(frIn.value); user.playing = false; el<HTMLInputElement>("play").checked = false; });
el<HTMLInputElement>("play").addEventListener("change", e => { user.playing = (e.target as HTMLInputElement).checked; });
let last = 0;

function frameAt(t: number): void {
  let ep: number, u: number;
  if (recorded) ({ ep, u } = scripted(t));
  else {
    const e = episodes[user.ep];
    if (user.playing) user.u = (user.u + Math.max(0, t - last) / e.seconds) % 1;
    last = t;
    ep = user.ep; u = user.u;
    frIn.value = u.toFixed(3);
    el("epVal").textContent = String(ep);
  }
  const e = episodes[ep], k = clamp(Math.round(u * (e.state.length - 1)), 0, e.state.length - 1);
  setFrame("imgUp", "noUp", `${ep}-up`, u);
  setFrame("imgSide", "noSide", `${ep}-side`, u);
  el("epNo").textContent = String(ep);
  el("frame").textContent = String(Math.round(u * (e.frames - 1)));
  el("frames").textContent = String(e.frames);
  el("time").textContent = (u * e.seconds).toFixed(1);
  el("bar").style.width = (u * 100).toFixed(1) + "%";

  // each joint: this episode's curve over its own normalised time, the band, the cursor
  const b = Math.round(u * 99);
  let off = -1, best = 0;
  NAMES.forEach((_, j) => {
    const pts = e.state.map((s, i) => [i / (e.state.length - 1), s[j]] as [number, number]);
    const mean = band.mean.map((m, i) => [i / 99, m[j]] as [number, number]);
    const lo = band.mean.map((m, i) => [i / 99, m[j] - band.std[i][j]] as [number, number]);
    const hi = band.mean.map((m, i) => [i / 99, m[j] + band.std[i][j]] as [number, number]);
    const dev = Math.abs(e.state[k][j] - band.mean[b][j]), z = dev / band.std[b][j];
    const out = z > 2 && dev > .06 * charts[j].range;   // 2σ out, and by a visible amount (σ is tiny where all 50 agree)
    if (out && (off < 0 || z > best)) { off = j; best = z; }
    charts[j].chart.draw({ bands: [{ lo, hi, colour: "#8b98ad", opacity: .16 }], cursor: u,
      series: [{ points: mean, colour: "#8b98ad", width: 1, dash: "3 3", opacity: .7 }, { points: pts, colour: out ? "#fbbf24" : "#34d399", width: 2.2 }] });
    el("v" + j).textContent = e.state[k][j].toFixed(1);
    el("j" + j).classList.toggle("off", out);
  });
  const mark = el("mark");
  mark.style.left = (drift.at * 100).toFixed(1) + "%";
  mark.style.opacity = ep === drift.episode ? "1" : "0";
  el("where").innerHTML = off >= 0 ? `<span style="color:var(--l3)">${tr("! {0}离 50 段平均 &gt; 2σ", tr(NAMES[off][0]))}</span>` : "";
  el("drift").innerHTML = ep === drift.episode
    ? tr("这是 50 段里<b>和其余最不一样</b>的一段：平均偏离 {0} 个标准差（50 段的中位数是 {1}）。", drift.score.toFixed(2), drift.median_score.toFixed(2)) +
      tr("最大的一处在进度 {0}%，{1}偏了 {2}σ。它不一定是“错的”示范，但训练前值得看一眼：积木放在了别处，动作就跟着不一样。",
        Math.round(drift.at * 100), tr(NAMES[DATA.joints.indexOf(drift.joint)][0]), drift.z.toFixed(1))
    : tr("灰带是 50 段在同一进度上通常所在的范围（均值 ± 1σ）。跑偏的地方会变成橙色：试试第 {0} 段。", drift.episode);
  el("task").textContent = `"${DATA.source.task}"`;
  el("src").textContent = tr("数据：{0}（Apache-2.0）· {1} 段 · {2} 帧 · 关节读数按 {3} Hz 取样", DATA.source.repo.replace("https://", ""), DATA.source.episodes, DATA.source.frames, HZ);
  el("clock").textContent = "T+" + t.toFixed(2).padStart(5, "0");
  el("dot").style.opacity = (t * 2 | 0) % 2 ? ".3" : "1";
  render();
}

// the clock starts once the frames are in, so the first recorded frame already shows pictures
Promise.all(Object.values(sprites).map(s => new Promise<void>(res => { const i = new Image(); i.onload = i.onerror = () => res(); i.src = s.file; })))
  .then(() => run(frameAt, { dur: Number(q.get("dur") || 9.5), still: 4 }));
